import React from 'react';
import {
  Box, Typography, Stack, Card, Avatar, Chip, IconButton, BottomNavigation,
  BottomNavigationAction, Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Timeline as ProgressIcon,
  GroupAdd as JoinIcon,
  Person as PersonIcon,
  ArrowDownward as PullIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import PhoneFrame, { AutomatrixBanner, NavBar } from './PhoneFrame';
import { APP_LOGOS, APP_COLORS, STUDENT } from '../sharedMockData';

/**
 * Post-login tile grid. Partner tiles launch one of three auth layers
 * depending on /api/mobile/app-launch/:appKey response (see Android design §1a).
 */

const TILES = [
  {
    key: 'khan',
    name: 'Khan Academy',
    initials: 'KA',
    color: APP_COLORS.khan,
    logo: APP_LOGOS.khan,
    status: '3 courses',
    note: '45 min today',
    authLayer: 'L3',
  },
  {
    key: 'icivics',
    name: 'iCivics',
    initials: 'iC',
    color: APP_COLORS.icivics,
    logo: APP_LOGOS.icivics,
    status: 'Class 5A',
    note: 'Due Friday',
    authLayer: 'L3',
  },
  {
    key: 'readtheory',
    name: 'ReadTheory',
    initials: 'RT',
    color: APP_COLORS.readtheory,
    logo: APP_LOGOS.readtheory,
    status: 'Level 7',
    note: '12 quizzes',
    authLayer: 'L3',
  },
  {
    key: 'typingclub',
    name: 'TypingClub',
    initials: 'TC',
    color: APP_COLORS.typingclub,
    logo: APP_LOGOS.typingclub,
    status: '42 WPM',
    note: 'Lesson 14',
    authLayer: 'L3',
  },
  {
    key: 'w3schools',
    name: 'W3Schools',
    initials: 'W3',
    color: APP_COLORS.w3schools,
    logo: APP_LOGOS.w3schools,
    status: 'HTML · CSS',
    note: 'Continue',
    authLayer: 'L3',
  },
  {
    key: 'learnfree',
    name: 'LearnFree',
    initials: 'LF',
    color: APP_COLORS.learnfree,
    logo: APP_LOGOS.learnfree,
    status: 'Job skills',
    note: 'Word course',
    authLayer: 'L3',
  },
];

const Tile = ({ t }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 4,
      border: '1px solid #e6e8ef',
      p: 1.5,
      height: 138,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      bgcolor: '#fff',
      position: 'relative',
      transition: 'transform 0.1s',
      '&:active': { transform: 'scale(0.98)' },
      boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
      {t.logo ? (
        <Avatar
          src={t.logo}
          variant="rounded"
          sx={{ width: 40, height: 40, bgcolor: '#f4f5f9', borderRadius: 2, '& img': { objectFit: 'contain', p: 0.5 } }}
        />
      ) : (
        <Avatar
          variant="rounded"
          sx={{ width: 40, height: 40, bgcolor: t.color, borderRadius: 2, fontSize: '0.85rem', fontWeight: 700 }}
        >
          {t.initials}
        </Avatar>
      )}
      <Chip
        size="small"
        label={t.status}
        sx={{
          height: 20,
          fontSize: '0.62rem',
          fontWeight: 600,
          bgcolor: '#e3f2fd',
          color: '#1565c0',
          '& .MuiChip-label': { px: 0.8 },
        }}
      />
    </Stack>
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
        {t.name}
      </Typography>
      <Typography variant="caption" sx={{ color: '#5f6368', fontSize: '0.68rem' }}>
        {t.note}
      </Typography>
    </Box>
  </Card>
);

const AndroidDashboardMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', py: 2 }}>
    <PhoneFrame background="#f6f7fb">
      <AutomatrixBanner />

      {/* Top app bar */}
      <Box sx={{
        px: 2, pt: 1, pb: 1,
        bgcolor: '#fff',
        display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '1px solid #ececf2',
      }}>
        <IconButton size="small"><MenuIcon sx={{ fontSize: 22 }} /></IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" sx={{ color: '#6b6f76', fontSize: '0.65rem' }}>
            Good morning,
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1, fontSize: '0.95rem' }}>
            {STUDENT.short}
          </Typography>
        </Box>
        <IconButton size="small"><SearchIcon sx={{ fontSize: 20 }} /></IconButton>
        <IconButton size="small">
          <Badge color="error" variant="dot" overlap="circular">
            <NotificationsIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Box>

      {/* Scrollable main content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, pb: 1 }}>
        {/* Pull-to-refresh affordance */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 0.75, py: 1, color: '#1976d2',
        }}>
          <PullIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
            Pull to refresh · synced 2m ago
          </Typography>
        </Box>

        {/* Daily goal card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: '#fff',
            p: 2,
            mb: 2,
            boxShadow: '0 6px 14px rgba(25,118,210,0.25)',
          }}
        >
          <Typography variant="caption" sx={{ color: '#bbdefb', fontSize: '0.65rem' }}>
            DAILY GOAL
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25, fontSize: '1.05rem' }}>
            45 / 60 min today
          </Typography>
          <Box sx={{
            mt: 1, height: 6, borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.25)', overflow: 'hidden',
          }}>
            <Box sx={{ width: '75%', height: '100%', bgcolor: '#fff', borderRadius: 3 }} />
          </Box>
          <Typography variant="caption" sx={{ color: '#e3f2fd', fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
            Keep going — 15 more minutes to hit your streak!
          </Typography>
        </Card>

        {/* Section label */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Your apps
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton size="small" sx={{ p: 0.25 }}>
              <RefreshIcon sx={{ fontSize: 16, color: '#6b6f76' }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#6b6f76', fontSize: '0.7rem' }}>
              6 assigned
            </Typography>
          </Stack>
        </Stack>

        {/* Tile grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.25,
          mb: 2,
        }}>
          {TILES.map(t => <Tile key={t.key} t={t} />)}
        </Box>

        <Typography variant="caption" sx={{ color: '#8a8d93', fontSize: '0.62rem', textAlign: 'center', display: 'block', py: 1 }}>
          Tap a tile to launch · auth handled automatically
        </Typography>
      </Box>

      {/* Bottom nav */}
      <BottomNavigation
        showLabels
        value={0}
        sx={{
          borderTop: '1px solid #ececf2',
          bgcolor: '#fff',
          height: 56,
          '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
          '& .MuiBottomNavigationAction-label': { fontSize: '0.62rem', fontWeight: 600 },
        }}
      >
        <BottomNavigationAction label="Dashboard" icon={<HomeIcon />} />
        <BottomNavigationAction label="Progress" icon={<ProgressIcon />} />
        <BottomNavigationAction label="Join Class" icon={<JoinIcon />} />
        <BottomNavigationAction label="Profile" icon={<PersonIcon />} />
      </BottomNavigation>

      <NavBar />
    </PhoneFrame>
  </Box>
);

export default AndroidDashboardMockup;
